import React, { useContext, useEffect } from "react"

import { DefaultModality } from "amazon-chime-sdk-js"
import { useHistory } from "react-router-dom"

import EventEmitter from "src/EventEmitter"
import { geminiLanguageOptions } from "src/geminiLanguageOptions"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import CaptionRail from "components/ChimeCloudroom/Shared/CaptionRail"
import GoogleDocButton from "components/ChimeCloudroom/Shared/GoogleDocButton"
import LocalTile from "components/ChimeCloudroom/Shared/LocalTile"
import MiroButton from "components/ChimeCloudroom/Shared/MiroButton"
import RemoteTile from "components/ChimeCloudroom/Shared/RemoteTile"

import ErrorModal from "../Shared/ErrorModal"
import SurveyModal from "../Shared/SurveyModal"

import AdminAudioControls from "./AdminAudioControls"
import EmergencyEndSession from "./EmergencyEndSession"
import MissingTutorModal from "./MissingTutorModal"
import ScreenShareButton from "./ScreenShareButton"
import SessionCanceledModal from "./SessionCanceledModal"
import SessionReassignmentModal from "./SessionReassigmentModal"
import StudentAssessmentModal from "./StudentAssessmentModal"

const StudentLayout: React.FC = () => {
  const {
    session,
    localUser,
    ghost,
    roster,
    activeLanguageCode,
    playbackRate,
  } = useContext(CloudroomContext)

  const history = useHistory()

  useEffect(() => {
    const onEndSession = () => {
      if (localUser.type === "Teacher") history.push("/")
      else window.location.href = session.postSessionRedirectPath
    }
    EventEmitter.subscribe("end_session", onEndSession)

    return () => EventEmitter.unsubscribe("end_session", onEndSession)
  }, [history, localUser.type, session.postSessionRedirectPath])

  const remoteAttendeeIds = Object.keys(roster).filter(
    id =>
      new DefaultModality(id).base() !==
      session.chimeJoinInfo.attendee.attendeeId
  )

  let primaryAttendeeId = remoteAttendeeIds.find(
    id => roster[id].sharingContent
  )

  if (!primaryAttendeeId)
    primaryAttendeeId = remoteAttendeeIds.find(id =>
      roster[id].externalUserId.startsWith("Tutor-")
    )

  const panelAttendeesIds = remoteAttendeeIds.filter(
    id => id !== primaryAttendeeId
  )

  const layout =
    panelAttendeesIds.length === 0 || (panelAttendeesIds.length === 1 && ghost)
      ? "single"
      : "grid"

  const languageLabel = geminiLanguageOptions.find(
    opt => opt.value === activeLanguageCode
  )?.label

  return (
    <>
      <ErrorModal />
      {session.survey && <SurveyModal />}
      <StudentAssessmentModal />
      <MissingTutorModal />
      <SessionCanceledModal />
      <SessionReassignmentModal />

      <div className="flex h-screen min-h-0 flex-col">
        {localUser.superadmin && languageLabel && (
          <div
            role="status"
            className="sticky w-full shrink-0 bg-sky-500 px-4 py-2.5 text-center text-white"
          >
            <div>This session is being translated into {languageLabel}</div>
          </div>
        )}
        <div className="grid min-h-0 flex-1 grid-areas-student-slim sm:grid-areas-student-wide">
          <main className="relative p-2 grid-in-main">
            {["Admin", "Teacher"].includes(localUser.type) && (
              <div className="absolute top-2 left-2">
                <EmergencyEndSession
                  sessionId={session.id}
                  userType={localUser.type}
                />
              </div>
            )}
            <div className="flex h-full flex-col justify-center">
              {primaryAttendeeId && (
                <RemoteTile attendeeId={primaryAttendeeId} />
              )}
            </div>
          </main>
          <aside className="flex max-w-[500px] flex-col justify-between p-2 grid-in-aside">
            <div
              className={
                layout === "grid"
                  ? "grid gap-2 sm:grid-cols-1 md:grid-cols-2"
                  : ""
              }
            >
              <LocalTile />
              {panelAttendeesIds.map(id => (
                <RemoteTile
                  attendeeId={id}
                  key={id}
                  numAttendees={remoteAttendeeIds.length}
                />
              ))}
            </div>

            <div className="mb-1 flex flex-col items-start">
              <MiroButton session={session} user={localUser} />
              <GoogleDocButton
                googledocUrl={session.googledocUrl}
                userType={localUser.type}
              />
              {!ghost && <ScreenShareButton />}
              {localUser.superadmin && <AdminAudioControls />}
            </div>
          </aside>
        </div>
        {(activeLanguageCode || playbackRate !== 1) && <CaptionRail />}
      </div>
    </>
  )
}

export default StudentLayout
